import React from 'react';
import { Badge, Button, Spinner, UncontrolledTooltip } from 'reactstrap';

import { ClickableIcon, Toolbar } from '~components';
import { faCog } from '~components/icon';

import { AuthTags } from '~constants/authTags';

import { FormTemplate, FormTemplateVersion } from '~forms/types';

interface ComponentProps {
  template: FormTemplate;
  versions: FormTemplateVersion[];
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string) => void;
  hasOpenDraft: boolean;
  canEdit: boolean;
  isPublishing: boolean;
  isCreatingVersion: boolean;
  isLimitReached: boolean;
  isLimitLoading?: boolean;
  limitMessage?: string;
  onPublish: () => void;
  onCreateVersion: () => void;
  onPreview: () => void;
  onOpenSettings: () => void;
}

const versionLabel = (version: FormTemplateVersion): string =>
  `v${version.version_number} - ${version.published_at ? 'published' : 'draft'}`;

const FormTopBar: React.FC<ComponentProps> = ({
  template,
  versions,
  selectedVersionId,
  onSelectVersion,
  hasOpenDraft,
  canEdit,
  isPublishing,
  isCreatingVersion,
  isLimitReached,
  isLimitLoading,
  limitMessage,
  onPublish,
  onCreateVersion,
  onPreview,
  onOpenSettings,
}) => {
  // Versions newest-first for the selector.
  const orderedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);
  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? null;
  const selectedIsPublished = !!selectedVersion?.published_at;

  // Title, description and the care-worker fill flag are edited in the Form Settings modal (cog icon).
  // The cog is gated on the Create tag, so non-editors don't see it.
  const settingsIcon = (
    <ClickableIcon
      key="form-builder-settings-btn"
      id="form-builder-settings-btn"
      icon={faCog}
      className="me-2"
      toolTip="Form settings"
      onClick={onOpenSettings}
      authTag={AuthTags.FORMS.CUSTOMFORMS.CREATE}
      dataCy="form-builder-settings-icon"
      ariaLabel="Form settings"
    />
  );

  const previewButton = (
    <Button
      key="form-builder-preview-btn"
      color="info"
      outline
      size="sm"
      className="me-2"
      onClick={onPreview}
      data-cy="form-builder-preview-btn"
      aria-label="Preview form"
    >
      Preview
    </Button>
  );

  // Only surface the reason tooltip once we know the limit is actually reached (not merely while the
  // count is still loading, which disables Publish without an explanatory message).
  const showLimitReason = isLimitReached && !!limitMessage;
  const publishButton = (
    <div
      key="form-builder-publish-wrapper"
      id="form-builder-publish-wrapper"
      className="d-inline-block"
      // A disabled <button> emits no hover/focus events (Bootstrap sets pointer-events:none), so the
      // wrapper carries the tooltip. Make the wrapper focusable while the limit applies so keyboard and
      // screen-reader users can reach the reason on focus too — not just sighted mouse users on hover.
      tabIndex={showLimitReason ? 0 : undefined}
    >
      <Button
        color="success"
        size="sm"
        onClick={onPublish}
        // Also disabled while the published-template count is still loading, so a fast click can't slip
        // through before the limit is known and hit the server's 400 instead.
        disabled={isPublishing || isLimitReached || isLimitLoading}
        data-cy="form-builder-publish-btn"
        aria-label="Publish form"
      >
        {isPublishing ? (
          <>
            <Spinner size="sm" className="me-1" />
            Publishing...
          </>
        ) : (
          'Publish'
        )}
      </Button>
      {/* Tooltip targets the wrapper, not the disabled button, and fires on hover AND focus so the
          reason is reachable by keyboard, not just by mouse. */}
      {showLimitReason && (
        <UncontrolledTooltip target="form-builder-publish-wrapper" trigger="hover focus">
          {limitMessage}
        </UncontrolledTooltip>
      )}
    </div>
  );

  const createVersionButton = (
    <Button
      key="form-builder-create-version-btn"
      color="success"
      size="sm"
      onClick={onCreateVersion}
      disabled={isCreatingVersion}
      data-cy="form-builder-create-version-btn"
      aria-label="Create new version"
    >
      {isCreatingVersion ? (
        <>
          <Spinner size="sm" className="me-1" />
          Creating...
        </>
      ) : (
        'Create New Version'
      )}
    </Button>
  );

  // Settings + Preview are always available; the publish/version action requires edit permission.
  const actionButtons = [settingsIcon, previewButton];
  if (canEdit) {
    actionButtons.push(hasOpenDraft ? publishButton : createVersionButton);
  }

  return (
    <Toolbar
      id="form-builder-top-bar"
      leftCluster={
        <div className="d-flex align-items-center gap-2">
          <span
            className="form-builder-title-display"
            aria-label={`Form title: ${template.title}`}
            data-cy="form-builder-title-display"
          >
            {template.title}
          </span>
          <select
            className="form-select form-select-sm form-builder-version-select"
            value={selectedVersionId ?? ''}
            onChange={(e) => onSelectVersion(e.target.value)}
            aria-label="Select version"
            data-cy="form-builder-version-select"
          >
            {orderedVersions.map((version) => (
              <option key={version.id} value={version.id}>
                {versionLabel(version)}
              </option>
            ))}
          </select>
          <Badge color={selectedIsPublished ? 'success' : 'warning'} pill>
            {selectedIsPublished ? 'Published' : 'Draft'}
          </Badge>
        </div>
      }
      rightCluster={<>{actionButtons}</>}
    />
  );
};

export default FormTopBar;
